import React, { useState, useMemo } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  MenuItem,
  TextField,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Tooltip,
  Skeleton,
  Button,
  Switch,
  FormControlLabel,
} from "@mui/material";
import {
  ForumRounded,
  CalendarMonthRounded,
  ScheduleRounded,
  TrendingUpRounded,
  EventAvailableRounded,
  CloseRounded,
  QueryStatsRounded,
} from "@mui/icons-material";
import { layoutCSS } from "../component/common/commonStyle";
import { skipToken } from "@reduxjs/toolkit/query";
import {
  useGetHourlyMISReportQuery,
  useGetHourlyMISDetailsQuery,
} from "../store/services/reportApi";
import { useGetrcsmisreportQuery } from "../store/services/rcstemplateApi";

/* ───────────── settings ───────────── */
// Flip to true to make heatmap cells open the detail dialog again
const ENABLE_DETAILS = false;

const T = {
  ink: "#1e1b4b",
  primary: "#6d28d9",
  primaryDark: "#4c1d95",
  primarySoft: "#f3efff",
  line: "#e7e3f5",
  muted: "#6b6890",
  surface: "#ffffff",
  accent: "#0ea5e9",
  warm: "#f59e0b",
};

const SERVICES = [
  { value: "sms", label: "SMS" },
  { value: "rcs", label: "RCS" },
];

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

/* ───────────── helpers ───────────── */
const nf = new Intl.NumberFormat("en-IN");
const fmt = (n) => nf.format(n || 0);
const compact = (n) => {
  if (n >= 10000000) return `${+(n / 10000000).toFixed(1)}Cr`;
  if (n >= 100000) return `${+(n / 100000).toFixed(1)}L`;
  if (n >= 1000) return `${+(n / 1000).toFixed(1)}k`;
  return `${n}`;
};
const pad = (h) => String(h).padStart(2, "0");

const niceMax = (v) => {
  if (v <= 4) return 4;
  if (v <= 8) return 8;
  if (v <= 12) return 12;
  const pow = Math.pow(10, Math.floor(Math.log10(v)));
  return [1.2, 2, 4, 6, 8, 10].map((m) => m * pow).find((c) => c >= v);
};

// light lavender → deep violet
const heatColor = (t) => {
  const from = [243, 239, 255];
  const to = [91, 33, 182];
  const c = from.map((f, i) => Math.round(f + (to[i] - f) * t));
  return `rgb(${c[0]},${c[1]},${c[2]})`;
};

const cardSx = {
  borderRadius: "16px",
  border: `1px solid ${T.line}`,
  backgroundColor: T.surface,
  boxShadow: "0 12px 32px -22px rgba(76,29,149,.35)",
};

const inputSx = {
  minWidth: 130,
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    backgroundColor: "#fff",
    fontSize: 13,
    "& fieldset": { borderColor: T.line },
    "&:hover fieldset": { borderColor: "#c4b5fd" },
    "&.Mui-focused fieldset": { borderColor: T.primary, borderWidth: 1.5 },
  },
  "& .MuiInputLabel-root.Mui-focused": { color: T.primary },
};

/* ───────────── building blocks ───────────── */
const KpiCard = ({ icon, label, value, sub, tint }) => (
  <Box sx={{ ...cardSx, p: 2, display: "flex", gap: 1.6, alignItems: "center" }}>
    <Box
      sx={{
        width: 42,
        height: 42,
        borderRadius: "12px",
        display: "grid",
        placeItems: "center",
        backgroundColor: `${tint}1a`,
        color: tint,
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: 12, color: T.muted }}>{label}</Typography>
      <Typography sx={{ fontSize: 21, fontWeight: 700, color: T.ink, lineHeight: 1.2 }}>
        {value}
      </Typography>
      <Typography sx={{ fontSize: 11.5, color: T.muted }} noWrap>
        {sub}
      </Typography>
    </Box>
  </Box>
);

const ChartCard = ({ title, subtitle, children }) => (
  <Box sx={{ ...cardSx, p: 2.2 }}>
    <Typography sx={{ fontWeight: 700, fontSize: 15, color: T.ink }}>{title}</Typography>
    <Typography sx={{ fontSize: 12, color: T.muted, mb: 2 }}>{subtitle}</Typography>
    {children}
  </Box>
);

const BarChart = ({ data, peakIndex, color, height = 200 }) => {
  const max = niceMax(Math.max(...data.map((d) => d.value), 0));
  const ticks = [1, 0.75, 0.5, 0.25, 0].map((f) => Math.round(max * f));

  return (
    <Box sx={{ display: "flex", gap: 1 }}>
      <Box
        sx={{
          height,
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          alignItems: "flex-end",
          pb: 0,
        }}
      >
        {ticks.map((t, i) => (
          <Typography key={i} sx={{ fontSize: 10, color: T.muted, lineHeight: 1 }}>
            {compact(t)}
          </Typography>
        ))}
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box
          sx={{
            position: "relative",
            height,
            display: "flex",
            alignItems: "flex-end",
            gap: { xs: "2px", md: "4px" },
            borderBottom: `1px solid ${T.line}`,
          }}
        >
          {[0, 25, 50, 75].map((p) => (
            <Box
              key={p}
              sx={{
                position: "absolute",
                left: 0,
                right: 0,
                bottom: `${p}%`,
                borderTop: `1px dashed ${p === 0 ? "transparent" : "#efecf8"}`,
              }}
            />
          ))}
          {data.map((d, i) => {
            const isPeak = i === peakIndex && d.value > 0;
            return (
              <Tooltip
                key={d.label}
                arrow
                placement="top"
                title={`${d.tooltip}: ${fmt(d.value)} ${d.value === 1 ? "message" : "messages"}`}
              >
                <Box
                  sx={{
                    position: "relative",
                    flex: 1,
                    height: "100%",
                    display: "flex",
                    alignItems: "flex-end",
                    borderRadius: "6px 6px 0 0",
                    "&:hover": { backgroundColor: "#f6f3ff" },
                  }}
                >
                  <Box
                    sx={{
                      width: "100%",
                      height: `${(d.value / max) * 100}%`,
                      minHeight: d.value > 0 ? 3 : 0,
                      borderRadius: "4px 4px 0 0",
                      backgroundColor: isPeak ? T.warm : color,
                      opacity: d.value > 0 ? 1 : 0.2,
                      transition: "height .35s ease",
                    }}
                  />
                </Box>
              </Tooltip>
            );
          })}
        </Box>
        <Box sx={{ display: "flex", gap: { xs: "2px", md: "4px" }, mt: 0.6 }}>
          {data.map((d, i) => (
            <Typography
              key={d.label}
              sx={{
                flex: 1,
                textAlign: "center",
                fontSize: 10,
                color: i === peakIndex && d.value > 0 ? "#b45309" : T.muted,
                fontWeight: i === peakIndex && d.value > 0 ? 700 : 400,
                visibility: { xs: i % 2 === 0 ? "visible" : "hidden", md: "visible" },
              }}
            >
              {d.label}
            </Typography>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

/* ───────────── page ───────────── */
const MISReport = () => {
  const now = new Date();
  const [selectedMonth, setSelectedMonth] = useState(MONTHS[now.getMonth()]);
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [service, setService] = useState("sms");

  const [openDialog, setOpenDialog] = useState(false);
  const [selectedCell, setSelectedCell] = useState(null);
  const [hideEmpty, setHideEmpty] = useState(true);

  const years = useMemo(() => {
    const arr = [];
    for (let y = 2020; y <= now.getFullYear() + 2; y++) arr.push(y);
    return arr;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Only the query for the selected service runs
  const smsQuery = useGetHourlyMISReportQuery(
    { month: selectedMonth, year: selectedYear },
    { skip: service !== "sms" },
  );
  const rcsQuery = useGetrcsmisreportQuery(
    { month: selectedMonth, year: selectedYear },
    { skip: service !== "rcs" },
  );
  const { data, isLoading, isFetching } = service === "rcs" ? rcsQuery : smsQuery;

  const misData = data?.data || [];

  const monthIndex = MONTHS.indexOf(selectedMonth);
  const daysInMonth = new Date(selectedYear, monthIndex + 1, 0).getDate();
  const isCurrentMonth =
    monthIndex === now.getMonth() && selectedYear === now.getFullYear();
  const today = isCurrentMonth ? now.getDate() : null;

  const days = useMemo(
    () => Array.from({ length: daysInMonth }, (_, i) => i + 1),
    [daysInMonth],
  );
  const hours = useMemo(() => Array.from({ length: 24 }, (_, i) => i), []);

  const dataMap = useMemo(() => {
    const map = {};
    misData.forEach((d) => {
      if (!map[d.day]) map[d.day] = {};
      map[d.day][Number(d.hour)] = (map[d.day][Number(d.hour)] || 0) + (Number(d.count) || 0);
    });
    return map;
  }, [misData]);

  const stats = useMemo(() => {
    const dailyTotals = days.map((d) =>
      Object.values(dataMap[d] || {}).reduce((s, v) => s + v, 0),
    );
    const hourlyTotals = hours.map((h) =>
      days.reduce((s, d) => s + (dataMap[d]?.[h] || 0), 0),
    );
    const total = dailyTotals.reduce((s, v) => s + v, 0);
    const maxDaily = Math.max(...dailyTotals, 0);
    const maxHourly = Math.max(...hourlyTotals, 0);
    const peakDayIdx = maxDaily > 0 ? dailyTotals.indexOf(maxDaily) : -1;
    const peakHourIdx = maxHourly > 0 ? hourlyTotals.indexOf(maxHourly) : -1;
    const activeDays = dailyTotals.filter((v) => v > 0).length;
    let maxCell = 0;
    days.forEach((d) =>
      hours.forEach((h) => {
        const v = dataMap[d]?.[h] || 0;
        if (v > maxCell) maxCell = v;
      }),
    );
    const slots = [];
    days.forEach((d) =>
      hours.forEach((h) => {
        const v = dataMap[d]?.[h] || 0;
        if (v > 0) slots.push({ day: d, hour: h, count: v });
      }),
    );
    slots.sort((a, b) => b.count - a.count || a.day - b.day || a.hour - b.hour);
    const parts = [
      { label: "Night", range: "12 AM – 6 AM", from: 0, to: 5, color: "#6366f1" },
      { label: "Morning", range: "6 AM – 12 PM", from: 6, to: 11, color: "#f59e0b" },
      { label: "Afternoon", range: "12 PM – 6 PM", from: 12, to: 17, color: "#0ea5e9" },
      { label: "Evening", range: "6 PM – 12 AM", from: 18, to: 23, color: "#8b5cf6" },
    ].map((pt) => ({
      ...pt,
      value: hourlyTotals.slice(pt.from, pt.to + 1).reduce((sum, v) => sum + v, 0),
    }));
    return {
      topSlots: slots.slice(0, 5),
      parts,
      dailyTotals,
      hourlyTotals,
      total,
      maxDaily,
      maxHourly,
      peakDayIdx,
      peakHourIdx,
      activeDays,
      maxCell,
      avg: activeDays ? Math.round(total / activeDays) : 0,
    };
  }, [dataMap, days, hours]);

  const visibleDays = useMemo(
    () =>
      hideEmpty
        ? days.filter((d) => stats.dailyTotals[d - 1] > 0 || d === today)
        : days,
    [hideEmpty, days, stats, today],
  );

  const handleCellClick = (day, hour) => {
    if (!ENABLE_DETAILS || service !== "sms") return;
    setSelectedCell({ day, hour });
    setOpenDialog(true);
  };

  const { data: detailsData, isLoading: detailsLoading } =
    useGetHourlyMISDetailsQuery(
      selectedCell
        ? {
            day: selectedCell.day,
            hour: selectedCell.hour,
            month: selectedMonth,
            year: selectedYear,
          }
        : skipToken,
    );
  const detailRows = detailsData?.data || [];

  const loading = isLoading || isFetching;
  const monthLabel = `${selectedMonth} ${selectedYear}`;
  const serviceLabel = SERVICES.find((x) => x.value === service)?.label || "";

  const dailyChart = days.map((d, i) => ({
    label: `${d}`,
    value: stats.dailyTotals[i],
    tooltip: `${d} ${selectedMonth}`,
  }));
  const hourlyChart = hours.map((h) => ({
    label: pad(h),
    value: stats.hourlyTotals[h],
    tooltip: `${pad(h)}:00 – ${pad(h)}:59`,
  }));

  return (
    <Box sx={layoutCSS}>
      {/* Header + filters */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1.5,
          mb: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.4 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "12px",
              display: "grid",
              placeItems: "center",
              color: "#fff",
              background: `linear-gradient(135deg, ${T.primaryDark}, ${T.primary})`,
              boxShadow: "0 8px 18px -8px rgba(109,40,217,.7)",
            }}
          >
            <QueryStatsRounded fontSize="small" />
          </Box>
          <Box>
            <Typography sx={{ fontSize: 18, fontWeight: 700, color: T.ink, lineHeight: 1.2 }}>
              MIS report
            </Typography>
            <Typography sx={{ fontSize: 12.5, color: T.muted }}>
              {serviceLabel} messages sent each hour in {monthLabel}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
          {!isCurrentMonth && (
            <Button
              size="small"
              onClick={() => {
                setSelectedMonth(MONTHS[now.getMonth()]);
                setSelectedYear(now.getFullYear());
              }}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                color: T.primary,
                borderRadius: "10px",
                "&:hover": { backgroundColor: T.primarySoft },
              }}
            >
              Go to this month
            </Button>
          )}
          <TextField
            select
            label="Service"
            value={service}
            onChange={(e) => setService(e.target.value)}
            size="small"
            sx={{ ...inputSx, minWidth: 110 }}
          >
            {SERVICES.map((sv) => (
              <MenuItem key={sv.value} value={sv.value} sx={{ fontSize: "0.8rem" }}>
                {sv.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Month"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            size="small"
            sx={inputSx}
          >
            {MONTHS.map((m) => (
              <MenuItem key={m} value={m} sx={{ fontSize: "0.8rem" }}>
                {m}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Year"
            value={selectedYear}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
            size="small"
            sx={{ ...inputSx, minWidth: 100 }}
          >
            {years.map((y) => (
              <MenuItem key={y} value={y} sx={{ fontSize: "0.8rem" }}>
                {y}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      </Box>

      {loading ? (
        <Box>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "repeat(4, 1fr)" },
              gap: 2,
              mb: 2,
            }}
          >
            {[...Array(4)].map((_, i) => (
              <Skeleton key={i} variant="rounded" height={86} sx={{ borderRadius: "16px" }} />
            ))}
          </Box>
          <Skeleton variant="rounded" height={280} sx={{ borderRadius: "16px", mb: 2 }} />
          <Skeleton variant="rounded" height={380} sx={{ borderRadius: "16px" }} />
        </Box>
      ) : stats.total === 0 ? (
        <Box sx={{ ...cardSx, textAlign: "center", py: 8, px: 2 }}>
          <Box
            sx={{
              width: 56,
              height: 56,
              mx: "auto",
              mb: 1.5,
              borderRadius: "16px",
              display: "grid",
              placeItems: "center",
              backgroundColor: T.primarySoft,
              color: T.primary,
            }}
          >
            <CalendarMonthRounded />
          </Box>
          <Typography sx={{ fontWeight: 700, color: T.ink, fontSize: 16 }}>
            No {serviceLabel} messages in {monthLabel}
          </Typography>
          <Typography sx={{ fontSize: 13, color: T.muted, mt: 0.5 }}>
            Pick another month or year to see its hourly traffic.
          </Typography>
        </Box>
      ) : (
        <>
          {/* KPI cards */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "repeat(4, 1fr)" },
              gap: 2,
              mb: 2,
            }}
          >
            <KpiCard
              icon={<ForumRounded />}
              tint={T.primary}
              label="Total messages"
              value={fmt(stats.total)}
              sub={`across ${stats.activeDays} active day${stats.activeDays === 1 ? "" : "s"}`}
            />
            <KpiCard
              icon={<TrendingUpRounded />}
              tint={T.warm}
              label="Busiest day"
              value={`${stats.peakDayIdx + 1} ${selectedMonth.slice(0, 3)}`}
              sub={`${fmt(stats.maxDaily)} messages`}
            />
            <KpiCard
              icon={<ScheduleRounded />}
              tint={T.accent}
              label="Peak hour"
              value={`${pad(stats.peakHourIdx)}:00 – ${pad((stats.peakHourIdx + 1) % 24)}:00`}
              sub={`${fmt(stats.maxHourly)} messages this month`}
            />
            <KpiCard
              icon={<EventAvailableRounded />}
              tint="#10b981"
              label="Daily average"
              value={fmt(stats.avg)}
              sub="on days with activity"
            />
          </Box>

          {/* Charts */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", xl: "1.4fr 1fr" },
              gap: 2,
              mb: 2,
            }}
          >
            <ChartCard
              title="Messages per day"
              subtitle="Total sent on each day of the month. The busiest day is highlighted in amber."
            >
              <BarChart data={dailyChart} peakIndex={stats.peakDayIdx} color={T.primary} />
            </ChartCard>

            <ChartCard
              title="Messages by hour of day"
              subtitle="Combined for the whole month. Shows when your traffic is heaviest."
            >
              <BarChart data={hourlyChart} peakIndex={stats.peakHourIdx} color={T.accent} />
            </ChartCard>
          </Box>

          {/* Busiest slots + time of day */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              gap: 2,
              mb: 2,
            }}
          >
            <ChartCard
              title="Busiest time slots"
              subtitle="The hours with the most messages on a single day."
            >
              {stats.topSlots.map((sl, i) => (
                <Box
                  key={`${sl.day}-${sl.hour}`}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    py: 1,
                    borderTop: i ? "1px solid #f0edf9" : "none",
                  }}
                >
                  <Box
                    sx={{
                      width: 26,
                      height: 26,
                      borderRadius: "8px",
                      display: "grid",
                      placeItems: "center",
                      fontSize: 12,
                      fontWeight: 700,
                      backgroundColor: i === 0 ? "#fef3c7" : T.primarySoft,
                      color: i === 0 ? "#b45309" : T.primary,
                    }}
                  >
                    {i + 1}
                  </Box>
                  <Typography sx={{ flex: 1, fontSize: 13, fontWeight: 600, color: T.ink }}>
                    {`${sl.day} ${selectedMonth.slice(0, 3)}, ${pad(sl.hour)}:00 – ${pad(sl.hour)}:59`}
                  </Typography>
                  <Box sx={{ width: 90, height: 6, borderRadius: "999px", backgroundColor: "#f0edf9", overflow: "hidden" }}>
                    <Box
                      sx={{
                        height: "100%",
                        width: `${(sl.count / stats.topSlots[0].count) * 100}%`,
                        backgroundColor: i === 0 ? T.warm : T.primary,
                        borderRadius: "999px",
                      }}
                    />
                  </Box>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: T.ink, minWidth: 34, textAlign: "right" }}>
                    {fmt(sl.count)}
                  </Typography>
                </Box>
              ))}
            </ChartCard>

            <ChartCard
              title="Time of day"
              subtitle="How this month's messages split across the day."
            >
              {stats.parts.map((pt, i) => {
                const pct = stats.total ? Math.round((pt.value / stats.total) * 100) : 0;
                return (
                  <Box key={pt.label} sx={{ py: 1, borderTop: i ? "1px solid #f0edf9" : "none" }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.6 }}>
                      <Typography sx={{ fontSize: 13, fontWeight: 600, color: T.ink }}>
                        {pt.label}
                        <Box component="span" sx={{ ml: 1, fontSize: 11.5, fontWeight: 400, color: T.muted }}>
                          {pt.range}
                        </Box>
                      </Typography>
                      <Typography sx={{ fontSize: 13, fontWeight: 700, color: T.ink }}>
                        {fmt(pt.value)}
                        <Box component="span" sx={{ ml: 0.8, fontSize: 11.5, fontWeight: 500, color: T.muted }}>
                          {pct}%
                        </Box>
                      </Typography>
                    </Box>
                    <Box sx={{ height: 8, borderRadius: "999px", backgroundColor: "#f0edf9", overflow: "hidden" }}>
                      <Box sx={{ height: "100%", width: `${pct}%`, backgroundColor: pt.color, borderRadius: "999px", transition: "width .4s" }} />
                    </Box>
                  </Box>
                );
              })}
            </ChartCard>
          </Box>

          {/* Heatmap */}
          <Box sx={{ ...cardSx, p: 2.2 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-end",
                flexWrap: "wrap",
                gap: 1,
                mb: 1.5,
              }}
            >
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: 15, color: T.ink }}>
                  Day-by-hour breakdown
                </Typography>
                <Typography sx={{ fontSize: 12, color: T.muted }}>
                  {`Each row is a day and each column an hour. Darker cells mean more messages. Showing ${visibleDays.length} of ${daysInMonth} days.`}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
              <FormControlLabel
                sx={{ m: 0 }}
                control={
                  <Switch
                    size="small"
                    checked={hideEmpty}
                    onChange={(e) => setHideEmpty(e.target.checked)}
                  />
                }
                label={
                  <Typography sx={{ fontSize: 12, color: T.ink }}>
                    Hide days with no messages
                  </Typography>
                }
              />
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                <Typography sx={{ fontSize: 11, color: T.muted }}>Fewer</Typography>
                <Box
                  sx={{
                    width: 110,
                    height: 8,
                    borderRadius: "999px",
                    background: `linear-gradient(90deg, ${heatColor(0)}, ${heatColor(0.5)}, ${heatColor(1)})`,
                  }}
                />
                <Typography sx={{ fontSize: 11, color: T.muted }}>More</Typography>
              </Box>
              </Box>
            </Box>

            <Box sx={{ overflowX: "auto", pb: 0.5 }}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "46px repeat(24, minmax(34px, 1fr)) 70px",
                  gap: "3px",
                  minWidth: 980,
                }}
              >
                {/* header row */}
                <Box />
                {hours.map((h) => (
                  <Typography
                    key={h}
                    sx={{
                      fontSize: 10.5,
                      textAlign: "center",
                      color: h === stats.peakHourIdx ? "#b45309" : T.muted,
                      fontWeight: h === stats.peakHourIdx ? 700 : 500,
                    }}
                  >
                    {pad(h)}
                  </Typography>
                ))}
                <Typography sx={{ fontSize: 10.5, textAlign: "right", color: T.muted, pr: 0.5 }}>
                  Day total
                </Typography>

                {/* day rows */}
                {visibleDays.map((day) => (
                  <React.Fragment key={day}>
                    <Typography
                      sx={{
                        fontSize: 11.5,
                        fontWeight: day === today ? 700 : 500,
                        color: day === today ? T.primary : T.ink,
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      {day}
                      {day === today && (
                        <Box
                          component="span"
                          sx={{ ml: 0.5, width: 5, height: 5, borderRadius: "50%", backgroundColor: T.primary }}
                        />
                      )}
                    </Typography>

                    {hours.map((h) => {
                      const count = dataMap[day]?.[h] || 0;
                      const t = count > 0 && stats.maxCell ? 0.2 + 0.8 * Math.sqrt(count / stats.maxCell) : 0;
                      return (
                        <Box
                          key={h}
                          title={`${day} ${selectedMonth}, ${pad(h)}:00 – ${pad(h)}:59\n${fmt(count)} ${count === 1 ? "message" : "messages"}`}
                          onClick={() => count > 0 && handleCellClick(day, h)}
                          sx={{
                            height: 26,
                            borderRadius: "6px",
                            display: "grid",
                            placeItems: "center",
                            fontSize: 10,
                            fontWeight: 600,
                            cursor: ENABLE_DETAILS && count > 0 ? "pointer" : "default",
                            backgroundColor: count > 0 ? heatColor(t) : "#faf9fe",
                            color: t > 0.5 ? "#fff" : T.primaryDark,
                            outline: day === today ? `1px solid ${T.line}` : "none",
                            transition: "transform .12s",
                            "&:hover": { transform: "scale(1.12)", zIndex: 1, boxShadow: "0 4px 10px rgba(76,29,149,.3)" },
                          }}
                        >
                          {count > 0 ? compact(count) : ""}
                        </Box>
                      );
                    })}

                    <Typography
                      sx={{
                        fontSize: 11.5,
                        fontWeight: 700,
                        textAlign: "right",
                        pr: 0.5,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-end",
                        color: day - 1 === stats.peakDayIdx ? "#b45309" : T.ink,
                      }}
                    >
                      {stats.dailyTotals[day - 1] ? fmt(stats.dailyTotals[day - 1]) : "–"}
                    </Typography>
                  </React.Fragment>
                ))}

                {/* hour totals row */}
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: T.ink, pt: 0.8 }}>
                  Total
                </Typography>
                {hours.map((h) => (
                  <Typography
                    key={h}
                    sx={{
                      fontSize: 10,
                      fontWeight: 700,
                      textAlign: "center",
                      pt: 0.8,
                      color: h === stats.peakHourIdx ? "#b45309" : T.ink,
                    }}
                  >
                    {stats.hourlyTotals[h] ? compact(stats.hourlyTotals[h]) : "–"}
                  </Typography>
                ))}
                <Typography sx={{ fontSize: 11.5, fontWeight: 800, textAlign: "right", pr: 0.5, pt: 0.8, color: T.primary }}>
                  {fmt(stats.total)}
                </Typography>
              </Box>
            </Box>
          </Box>
        </>
      )}

      {/* Detail dialog (opens only when ENABLE_DETAILS is true) */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { borderRadius: "18px" } }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            fontSize: "0.95rem",
            fontWeight: 700,
            color: T.ink,
          }}
        >
          {selectedCell &&
            `${selectedCell.day} ${selectedMonth}, ${pad(selectedCell.hour)}:00 – ${pad(selectedCell.hour)}:59`}
          <IconButton size="small" onClick={() => setOpenDialog(false)}>
            <CloseRounded fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          {detailsLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
              <CircularProgress size={22} />
            </Box>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow sx={{ "& th": { fontWeight: 600, color: T.muted, backgroundColor: "#faf9fe" } }}>
                  <TableCell>Sender ID</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>Source</TableCell>
                  <TableCell>Msg count</TableCell>
                  <TableCell>Message</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {detailRows.length > 0 ? (
                  detailRows.map((row, idx) => (
                    <TableRow key={idx} hover>
                      <TableCell>{row.sender_id || "-"}</TableCell>
                      <TableCell>{row.ph_no || "-"}</TableCell>
                      <TableCell>{row.source || "-"}</TableCell>
                      <TableCell>{row.smsCount || row.msgCount || "-"}</TableCell>
                      <TableCell
                        sx={{ maxWidth: 250, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                        title={row.smsText || ""}
                      >
                        {row.smsText || "-"}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      No records found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default MISReport;